import { Link } from "react-router-dom";
import type { Post } from "@/types/content";
import { formatDate, formatViews, getCategoryBySlug } from "@/lib/content";
import { toneBadge } from "@/lib/tone";

type Variant = "standard" | "wide" | "compact" | "ranked";

interface PostCardProps {
  post: Post;
  variant?: Variant;
  rank?: number;
}

export default function PostCard({ post, variant = "standard", rank }: PostCardProps) {
  const category = getCategoryBySlug(post.categorySlug);
  const tone = category?.tone ?? "primary";
  const categoryName = category?.name ?? "内容";

  if (variant === "compact") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex cursor-pointer items-start gap-4 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <span className="relative h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[72px] md:w-24">
          <img
            src={post.cover}
            alt={post.title}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {post.title}
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className={`rounded-full border px-2 py-0.5 ${toneBadge[tone]}`}>{categoryName}</span>
            <span>{formatDate(post.date)}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "ranked") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex cursor-pointer items-center gap-4 border-b border-background-200 py-4 last:border-b-0"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold ${
            rank === 1
              ? "bg-accent-500 text-foreground-950"
              : rank && rank <= 3
                ? "bg-primary-100 text-primary-700"
                : "bg-background-100 text-foreground-500"
          }`}
        >
          {rank}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-700 md:text-[15px]">
            {post.title}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{categoryName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(post.views)}
            </span>
          </span>
        </span>
        <i className="ri-arrow-right-up-line shrink-0 text-foreground-300 transition-colors group-hover:text-primary-600"></i>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex cursor-pointer flex-col overflow-hidden rounded-xl2 border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:flex-row"
      >
        <span className="h-48 w-full shrink-0 overflow-hidden bg-background-200 md:h-auto md:w-72">
          <img
            src={post.cover}
            alt={post.title}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </span>
        <span className="flex flex-1 flex-col p-5 md:p-6">
          <span className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2.5 py-0.5 text-xs ${toneBadge[tone]}`}>{categoryName}</span>
            <span className="text-xs text-foreground-500">{formatDate(post.date)}</span>
          </span>
          <span className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
            {post.title}
          </span>
          <span className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.excerpt}</span>
          <span className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
            <span>{post.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {post.readMinutes} 分钟
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {formatViews(post.views)}
            </span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/post/${post.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-xl2 border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <span className="block aspect-[16/10] w-full overflow-hidden bg-background-200">
        <img
          src={post.cover}
          alt={post.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </span>
      <span className="flex flex-1 flex-col p-5">
        <span className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full border px-2.5 py-0.5 text-xs ${toneBadge[tone]}`}>{categoryName}</span>
          {post.hot && (
            <span className="flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-0.5 text-xs text-background-50">
              <i className="ri-fire-line"></i>
              热
            </span>
          )}
        </span>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
          <span>{formatDate(post.date)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {post.readMinutes} 分钟
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(post.views)}
          </span>
        </div>
      </span>
    </Link>
  );
}